import {
  Family,
  Adult,
  UpdateFamilyRequest,
  UpdateAdultRequest,
  UpdateChildRequest,
  UpdateChildResponse,
  AddAdultRequest,
  AddChildRequest,
  AddAdultResponse as MemberAddAdultResponse,
  AddChildResponse as MemberAddChildResponse,
  DeleteMemberResponse,
  UpdateRelationshipRequest,
  UpdateRelationshipResponse,
} from '../types/family.types';
import { UserStatus } from '../types/enums';
import { api } from './client';
import { transformError } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import type { SelfRegisterFamilyResponse } from '../types/api';

interface MyFamiliesResponse {
  user: {
    workosUserId: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  families: Array<{
    id: string;
    name: string;
    status: UserStatus;
  }>;
}

interface ConsentRecord {
  id: string;
  name: string;
  type: 'adult' | 'child';
  isGiven: boolean;
  isNameGiven: boolean;
}

interface RegistrationData {
  adults: Array<{
    id: string;
    firstName: string;
    lastName: string;
    birthMonth: number;
    birthYear: number;
    phone: string;
    gender: string;
    relationshipToChild: string;
    email?: string;
  }>;
  children: Array<{
    id: string;
    firstName: string;
    lastName: string;
    birthMonth: number;
    birthYear: number;
    gender: string;
  }>;
  pregnancy: {
    isPregnant: boolean;
    expectedDeliveryMonth: number | null;
    expectedDeliveryYear: number | null;
    expectedBabies: number | null;
    adultIndex?: number;
  };
  homePostalCode: string;
  isLowIncome: boolean | null;
  languagesSpoken: string[];
  otherLanguage: string | null;
  hearAboutUs: string[];
  hearAboutUsOther: string | null;
  referralDetails: string | null;
  consentGiven: boolean;
  consentDate: string;
  photoReleaseConsents?: ConsentRecord[];
}

interface RegistrationResponse {
  id: string;
  message: string;
}

export const familyService = {
  getMyFamilies: async (): Promise<MyFamiliesResponse> => {
    try {
      const response = await api.get<MyFamiliesResponse>('/my-families');
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'fetching families');
      throw new Error(enhanced.userMessage);
    }
  },

  getMyFamilyDetails: async (familyId: string): Promise<Family> => {
    try {
      const response = await api.get<Family>(`/my-families/${familyId}`);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'fetching family details');
      throw new Error(enhanced.userMessage);
    }
  },

  validateRegistration: async (data: RegistrationData): Promise<{ valid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    // Collect all emails to check
    const emailsToCheck = data.adults
      .map((a) => a.email)
      .filter((email): email is string => !!email && email.trim() !== '');

    // Check for duplicate emails within the registration (same family)
    const uniqueEmails = new Set(emailsToCheck.map((e) => e.toLowerCase()));
    if (uniqueEmails.size !== emailsToCheck.length) {
      errors.push('Duplicate email addresses found in registration');
    }

    // Note: We intentionally allow adults with the same email to exist in multiple families.
    // The same person (same email) can be a primary adult in one family and a non-primary in another.

    return {
      valid: errors.length === 0,
      errors,
    };
  },

  registerFamily: async (data: RegistrationData): Promise<RegistrationResponse> => {
    try {
      logger.log('Starting family registration via self-register endpoint...', data);

      const mapLanguage = (lang: string): string => {
        const map: Record<string, string> = {
          English: 'en',
          French: 'fr',
          Spanish: 'es',
          Arabic: 'ar',
          Mandarin: 'zh',
          Cantonese: 'zh',
          Tagalog: 'tl',
          Portuguese: 'pt',
          German: 'de',
          Italian: 'it',
          Vietnamese: 'vi',
          Korean: 'ko',
          Russian: 'ru',
          Polish: 'pl',
          Urdu: 'ur',
          Hindi: 'hi',
        };
        return map[lang] || 'en';
      };

      const isoLanguages = [...new Set(data.languagesSpoken.map(mapLanguage))];

      // Prepare payload for SelfRegisterFamilyRequestDto
      const payload: Record<string, unknown> = {
        adults: data.adults.map((adult) => {
          const adultConsent = data.photoReleaseConsents?.find((c) => c.id === adult.id && c.type === 'adult');
          return {
            firstName: adult.firstName,
            lastName: adult.lastName,
            birthYear: adult.birthYear,
            birthMonth: adult.birthMonth,
            email: adult.email || undefined,
            phone: adult.phone,
            gender: adult.gender.toLowerCase(),
            relationshipToChild: adult.relationshipToChild.toLowerCase(),
            consentForPhoto: adultConsent?.isGiven ?? false,
          };
        }),
        children: data.children.map((child) => {
          const childConsent = data.photoReleaseConsents?.find((c) => c.id === child.id && c.type === 'child');
          return {
            firstName: child.firstName,
            lastName: child.lastName,
            birthYear: child.birthYear,
            birthMonth: child.birthMonth,
            gender: child.gender.toLowerCase(),
            hasSpecialNeeds: false, // Defaulting as before
            consentForPhoto: childConsent?.isGiven ?? false,
          };
        }),
        postalCodePrefix: data.homePostalCode.substring(0, 3),
        isLowIncome: data.isLowIncome || false,
        languages: isoLanguages,
        hearAboutUs: data.hearAboutUs,
        hearAboutUsOther: data.hearAboutUsOther || undefined,
        referralDetails: data.referralDetails || undefined,
        consentForReporting: data.consentGiven,
      };

      if (data.pregnancy.isPregnant) {
        payload.pregnancy = {
          isPregnant: true,
          expectedDeliveryMonth: data.pregnancy.expectedDeliveryMonth,
          expectedDeliveryYear: data.pregnancy.expectedDeliveryYear,
          expectedBabies: data.pregnancy.expectedBabies,
          adultIndex: data.pregnancy.adultIndex,
        };
      }

      // Pre-validate local logic if needed, but backend handles it now.
      const validation = await familyService.validateRegistration(data);
      if (!validation.valid) {
        // Some errors like "email exists" can be caught here, but backend double checks.
        // We keep this check for UX.
        throw new Error(validation.errors.join('; '));
      }

      logger.log('Sending self-register payload...', payload);

      const response = await api.post<SelfRegisterFamilyResponse>('/families/self-register', payload);

      logger.log('Family registered successfully:', response);

      return {
        id: response.familyId,
        message: 'Family registered successfully',
      };
    } catch (error) {
      const enhanced = transformError(error, 'family registration');
      throw new Error(enhanced.userMessage);
    }
  },

  updateMyFamily: async (familyId: string, data: UpdateFamilyRequest): Promise<Family> => {
    try {
      const response = await api.patch<Family>(`/my-families/${familyId}`, data);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'updating family details');
      throw new Error(enhanced.userMessage);
    }
  },

  updateMyFamilyAdult: async (adultId: string, data: UpdateAdultRequest): Promise<Adult> => {
    try {
      const response = await api.patch<Adult>(`/my-families/adults/${adultId}`, data);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'updating adult profile');
      throw new Error(enhanced.userMessage);
    }
  },

  updateMyFamilyChild: async (childId: string, data: UpdateChildRequest): Promise<UpdateChildResponse> => {
    try {
      const response = await api.patch<UpdateChildResponse>(`/my-families/children/${childId}`, data);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'updating child profile');
      throw new Error(enhanced.userMessage);
    }
  },

  addAdultToFamily: async (familyId: string, data: AddAdultRequest): Promise<MemberAddAdultResponse> => {
    try {
      const response = await api.post<MemberAddAdultResponse>(`/my-families/${familyId}/adults`, data);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'adding adult to family');
      throw new Error(enhanced.userMessage);
    }
  },

  addChildToFamily: async (familyId: string, data: AddChildRequest): Promise<MemberAddChildResponse> => {
    try {
      const response = await api.post<MemberAddChildResponse>(`/my-families/${familyId}/children`, data);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'adding child to family');
      throw new Error(enhanced.userMessage);
    }
  },

  deleteAdult: async (adultId: string): Promise<DeleteMemberResponse> => {
    try {
      const response = await api.delete<DeleteMemberResponse>(`/my-families/adults/${adultId}`);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'removing adult from family');
      throw new Error(enhanced.userMessage);
    }
  },

  deleteChild: async (childId: string): Promise<DeleteMemberResponse> => {
    try {
      const response = await api.delete<DeleteMemberResponse>(`/my-families/children/${childId}`);
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'removing child from family');
      throw new Error(enhanced.userMessage);
    }
  },

  updateRelationship: async (
    relationshipId: string,
    data: UpdateRelationshipRequest,
  ): Promise<UpdateRelationshipResponse> => {
    try {
      const response = await api.patch<UpdateRelationshipResponse>(
        `/my-families/relationships/${relationshipId}`,
        data,
      );
      return response;
    } catch (error) {
      const enhanced = transformError(error, 'updating relationship');
      throw new Error(enhanced.userMessage);
    }
  },
};
